import { EmptyState, ErrorMessage, LoadingState, PageHeader } from '@vrzl/ui';
import { Clock, LayoutGrid, Search, ShieldOff } from 'lucide-react';
import { useMemo } from 'react';
import { useParams } from 'react-router';

import { useAuth } from '@/domains/auth';
import { useCriteria } from '@/domains/criteria';
import { useEvent } from '@/domains/events';
import { useCompletedExtraction, useValidationReport } from '@/domains/report';

import { useOverviewConfig } from '../api/queries';
import { resolveBlock, type ResolvedBlock } from '../utils/resolveBlock';

import { EditOverviewButton } from './editOverviewButton';
import { OverviewBlockCard } from './overviewBlockCard';
import { OverviewLayout } from './overviewGrid';

export function Overview() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const activePerspective = user?.activePerspective ?? null;
  const activePerspectiveId = activePerspective?.id ?? null;

  const { data: event } = useEvent(eventId ?? '');
  const { data: criteriaData } = useCriteria(event?.applicationId ?? '', {
    perspectiveId: activePerspectiveId ?? undefined,
  });
  const { data: extractionData, isPending: isExtractionPending } = useCompletedExtraction(
    eventId ?? '',
  );
  const config = useOverviewConfig(activePerspectiveId ?? '');

  const extractionId = extractionData?.items[0]?.id ?? null;
  const criterionIds = useMemo(
    () => (criteriaData?.items ?? []).map((c) => c.id),
    [criteriaData],
  );
  const typeByCode = useMemo(
    () => new Map((criteriaData?.items ?? []).map((c) => [c.code, c.type] as const)),
    [criteriaData],
  );
  const criterionOptions = useMemo(
    () => (criteriaData?.items ?? []).map((c) => ({ code: c.code, name: c.name })),
    [criteriaData],
  );
  const validation = useValidationReport(extractionId, criterionIds);

  const blocks = useMemo<ResolvedBlock[]>(() => {
    if (!validation.data || !config.data) return [];
    const itemsByCode = new Map(validation.data.items.map((item) => [item.code, item]));
    return config.data.blocks.flatMap((block, index) => {
      const resolved = resolveBlock(block, index, itemsByCode, typeByCode);
      return resolved ? [resolved] : [];
    });
  }, [validation.data, config.data, typeByCode]);

  if (!activePerspective) {
    return (
      <div>
        <PageHeader title="Overview" />
        <EmptyState
          icon={ShieldOff}
          title="Geen perspectief toegewezen"
          description="Je hebt geen actief perspectief. Neem contact op met je beheerder."
        />
      </div>
    );
  }

  const isExtractionInFlight =
    event?.extractionStatus === 'QUEUED' || event?.extractionStatus === 'IN_PROGRESS';
  const hasExtractionForPerspective = extractionId !== null;
  const hasNoConfig = config.data !== undefined && config.data.blocks.length === 0;

  return (
    <div>
      <PageHeader
        title={event?.name||""}
        action={
          <div className="print:hidden">
            <EditOverviewButton
              blocks={config.data?.blocks ?? []}
              options={criterionOptions}
              perspective={activePerspective}
              disabled={!config.data || !criteriaData}
            />
          </div>
        }
      />

      {(config.isPending || isExtractionPending) && <LoadingState />}

      {config.error && <ErrorMessage error={config.error} message="Kan overzicht niet laden" />}

      {hasNoConfig && (
        <EmptyState
          icon={LayoutGrid}
          title="Geen overzicht ingesteld"
          description="Voor jouw organisatie en perspectief is nog geen overzicht ingericht. Neem contact op met je beheerder."
        />
      )}

      {config.data && !hasNoConfig && !isExtractionPending && !hasExtractionForPerspective && (
        <EmptyState
          icon={Clock}
          title={
            isExtractionInFlight
              ? 'Extractie loopt nog'
              : !event?.extractionStatus
                ? 'Nog geen extractie voor dit evenement'
                : 'Geen extractie voor dit perspectief'
          }
          description="Resultaten verschijnen zodra de extractie voor dit perspectief beschikbaar is."
        />
      )}

      {config.data && !hasNoConfig && hasExtractionForPerspective && validation.isPending && (
        <LoadingState />
      )}
      {config.data && !hasNoConfig && hasExtractionForPerspective && validation.error && (
        <ErrorMessage error={validation.error} message="Kan informatie niet laden" />
      )}

      {config.data && !hasNoConfig && validation.data && (
        blocks.length > 0 ? (
          <OverviewLayout
            widths={blocks.map((block) => block.width)}
            getKey={(index) => blocks[index]!.key}
            renderItem={(index) => (
              <OverviewBlockCard block={blocks[index]!} eventId={eventId ?? ''} />
            )}
          />
        ) : (
          <EmptyState
            icon={Search}
            title="Geen criteria gevonden voor dit overzicht"
            description="De ingestelde criteria komen niet voor in dit perspectief. Neem contact op met je beheerder."
          />
        )
      )}
    </div>
  );
}
